import { InAppNotification } from '../../api/Types';
import { webPushSubscriptionService } from '../../services/WebPushSubscriptionService';
import {
  buildBrowserNotificationPayload,
  canSetAppBadge,
  canShowSystemNotification,
  getNotificationPermission,
  hasNotificationApi,
  shouldClearAppBadge,
} from '../../hooks/notifications/inAppNotificationPlatform.helpers';

type AppBadgeNavigator = Navigator & {
  setAppBadge?: (contents?: number) => Promise<void>;
  clearAppBadge?: () => Promise<void>;
};

interface ShowSystemNotificationOptions {
  authorId?: number;
  pwaMode: boolean;
  notification: InAppNotification;
}

export const requestNotificationPermissionIfNeeded = async ({
  authorId,
  pwaMode,
}: {
  authorId?: number;
  pwaMode: boolean;
}): Promise<void> => {
  if (!authorId || !pwaMode || !hasNotificationApi()) {
    return;
  }

  if (Notification.permission === 'default') {
    await Notification.requestPermission().catch(() => undefined);
  }
};

export const syncWebPushSubscriptionIfNeeded = async ({
  authorId,
  pwaMode,
}: {
  authorId?: number;
  pwaMode: boolean;
}): Promise<void> => {
  if (!authorId || !pwaMode || !hasNotificationApi()) {
    return;
  }

  if (Notification.permission !== 'granted') {
    return;
  }

  try {
    await webPushSubscriptionService.syncCurrentSubscription();
  } catch (error) {
    console.warn('Unable to sync web push subscription:', error);
  }
};

export const syncAppIconBadgeForUnreadCount = async ({
  authorId,
  unreadCount,
  pwaMode,
}: {
  authorId?: number;
  unreadCount: number;
  pwaMode: boolean;
}): Promise<void> => {
  if (typeof navigator === 'undefined' || !pwaMode) {
    return;
  }

  const badgeNavigator = navigator as AppBadgeNavigator;
  const notificationPermission = getNotificationPermission();

  try {
    if (!authorId) {
      if (shouldClearAppBadge({ unreadCount: 0, badgeNavigator }) && badgeNavigator.clearAppBadge) {
        await badgeNavigator.clearAppBadge();
      }
      return;
    }

    if (
      canSetAppBadge({
        unreadCount,
        permission: notificationPermission,
        badgeNavigator,
      }) &&
      badgeNavigator.setAppBadge
    ) {
      await badgeNavigator.setAppBadge(unreadCount);
      return;
    }

    if (shouldClearAppBadge({ unreadCount, badgeNavigator }) && badgeNavigator.clearAppBadge) {
      await badgeNavigator.clearAppBadge();
    }
  } catch (error) {
    console.warn('Unable to sync app icon badge:', error);
  }
};

export const showBrowserSystemNotification = async ({
  authorId,
  pwaMode,
  notification,
}: ShowSystemNotificationOptions): Promise<void> => {
  const canShowNotification = canShowSystemNotification({
    authorId,
    pwaMode,
    notificationApiAvailable: hasNotificationApi(),
    permission: getNotificationPermission(),
  });

  if (!canShowNotification) {
    return;
  }

  const { title, body, data } = buildBrowserNotificationPayload(notification);

  try {
    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.ready;
      await registration.showNotification(title, {
        body,
        data,
        icon: '/logo192.png',
        badge: '/logo192.png',
        tag: `in-app-notification-${notification.id}`,
        renotify: false,
      });
      return;
    }
  } catch (error) {
    console.warn('Unable to display service worker notification:', error);
  }

  try {
    new Notification(title, { body });
  } catch (error) {
    console.warn('Unable to display browser notification:', error);
  }
};
